import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { BuydownScenario } from './results/utils/buydownCalculations';
import { 
  DollarSign,
  TrendingUp, 
  Home, 
  Calculator, 
  PieChart, 
  FileText,
  Download,
  AlertCircle,
  CheckCircle,
  Edit3,
  Share,
  Pencil
} from 'lucide-react';
import type { MortgageData } from './CalculatorTabs';
import { formatCurrency, formatInterestRate } from './utils/formatters';
import { parseCurrencyInput, formatCurrencyInput } from '@/utils/currencyFormatting';
import BuydownIncentiveCalculator from './results/BuydownIncentiveCalculator';
import { PMICoverageBreakdown } from './results/PMICoverageBreakdown';
import ClosingDisclosurePreview from './results/ClosingDisclosurePreview';

interface EnhancedResultsDisplayProps {
  mortgageData: MortgageData;
  onGenerateReport?: () => void;
  isGeneratingReport?: boolean;
  onEditInputs?: () => void;
  onViewReport?: () => void;
  onBuydownScenariosCalculated?: (scenarios: BuydownScenario[]) => void;
  onShare?: () => void;
  onHomePriceChange?: (value: number) => void;
}

export const EnhancedResultsDisplay: React.FC<EnhancedResultsDisplayProps> = ({
  mortgageData,
  onGenerateReport,
  isGeneratingReport = false,
  onEditInputs,
  onViewReport,
  onShare,
  onBuydownScenariosCalculated,
  onHomePriceChange
}) => {
  const [buydownScenarios, setBuydownScenarios] = useState<BuydownScenario[]>([]);
  const [isEditingHomePrice, setIsEditingHomePrice] = useState(false);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const priceInputRef = useRef<HTMLInputElement>(null);

  const handleStartEditingPrice = () => {
    if (!onHomePriceChange) return;
    setEditingPriceValue(formatCurrencyInput(String(mortgageData.homePrice)));
    setIsEditingHomePrice(true);
    setTimeout(() => priceInputRef.current?.select(), 0);
  };

  const handleCommitPrice = () => {
    const parsed = parseFloat(parseCurrencyInput(editingPriceValue));
    if (!isNaN(parsed) && parsed > 0 && onHomePriceChange) {
      onHomePriceChange(parsed);
    }
    setIsEditingHomePrice(false);
  };

  const handleCancelEditPrice = () => {
    setIsEditingHomePrice(false);
  };
  
  // Track displayed insurance for syncing with Coventry selector
  const [displayedInsurance, setDisplayedInsurance] = useState(
    mortgageData.monthlyPayment.insurance
  );
  
  // Reset displayed insurance when mortgageData changes
  useEffect(() => {
    setDisplayedInsurance(mortgageData.monthlyPayment.insurance);
  }, [mortgageData.monthlyPayment.insurance]);
  
  // Calculate adjusted total when insurance changes
  const adjustedMonthlyTotal = useMemo(() => {
    const insuranceDiff = displayedInsurance - mortgageData.monthlyPayment.insurance;
    return mortgageData.monthlyPayment.total + insuranceDiff;
  }, [displayedInsurance, mortgageData.monthlyPayment.insurance, mortgageData.monthlyPayment.total]);

  useEffect(() => {
    if (onBuydownScenariosCalculated && buydownScenarios.length > 0) {
      onBuydownScenariosCalculated(buydownScenarios);
    }
  }, [buydownScenarios, onBuydownScenariosCalculated]);
  const navigate = useNavigate();
  
  const handleViewProfessionalReport = () => {
    navigate('/mortgage-report', { state: { mortgageData } });
  };
  // Calculate derived values
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const totalInterest = ((mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest) * mortgageData.loanTerm * 12) - loanAmount;
  
  // Calculate net closing costs (after seller credits)
  const netClosingCosts = mortgageData.closingCosts.total - (mortgageData.closingCosts.sellerCreditAmount || 0);
  const hasSellerCredits = (mortgageData.closingCosts.sellerCreditAmount || 0) > 0;
  
  // Use totalCashNeeded which already accounts for seller credits, or calculate fallback
  const totalCashAtClosing = mortgageData.closingCosts.totalCashNeeded || 
    (mortgageData.downPayment + netClosingCosts);
  
  const totalCost = mortgageData.homePrice + netClosingCosts;
  const loanToValue = (loanAmount / mortgageData.homePrice) * 100;
  const debtToIncomeEstimate = (mortgageData.monthlyPayment.total / (mortgageData.homePrice * 0.0004)) * 100; // Rough estimate

  // Check if calculation is complete
  const isCalculationComplete = mortgageData.monthlyPayment.total > 0 && mortgageData.closingCosts.total > 0;

  return (
    <div className="space-y-6">
      {/* Header with Action Buttons */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Enhanced Results Summary</h3>
          <p className="text-muted-foreground">Detailed analysis of your mortgage calculation</p>
        </div>
        
        <div className="flex gap-2">
          {onEditInputs && (
            <Button
              variant="outline"
              onClick={onEditInputs}
              className="flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Edit Inputs
            </Button>
          )}
          
          {onViewReport && (
            <Button
              variant="outline"
              onClick={onViewReport}
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              View Report
            </Button>
          )}

          {onShare && (
            <Button
              variant="outline"
              onClick={onShare}
              className="flex items-center gap-2"
            >
              <Share className="w-4 h-4" />
              Share
            </Button>
          )}
          
        </div>
      </div>

      {/* Calculation Status */}
      {!isCalculationComplete && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="flex items-center gap-3 pt-6">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            <div>
              <p className="font-semibold text-orange-800">Calculation Incomplete</p>
              <p className="text-sm text-orange-700">
                Please ensure all required fields are filled to see complete results.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics Grid */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Payment</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(mortgageData.monthlyPayment.total)}
            </div>
            <p className="text-xs text-muted-foreground">
              Principal & Interest: {formatCurrency(mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Interest</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(totalInterest)}
            </div>
            <p className="text-xs text-muted-foreground">
              Over {mortgageData.loanTerm} years
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Closing Costs</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(netClosingCosts)}
            </div>
            <p className="text-xs text-muted-foreground">
              {hasSellerCredits ? 
                `After ${formatCurrency(mortgageData.closingCosts.sellerCreditAmount)} seller credit` : 
                'Estimated at closing'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cash to Close</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(totalCashAtClosing)}
            </div>
            <p className="text-xs text-muted-foreground">
              {hasSellerCredits ? 
                'Down payment + net closing costs' : 
                'Down payment + closing costs'
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Payment Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Monthly Payment Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Principal & Interest</span>
                <span className="font-semibold">
                  {formatCurrency(mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest)}
                </span>
              </div>
              <Progress 
                value={((mortgageData.monthlyPayment.principal + mortgageData.monthlyPayment.interest) / mortgageData.monthlyPayment.total) * 100} 
                className="h-2"
              />
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Property Taxes</span>
                <span className="font-semibold">
                  {formatCurrency(mortgageData.monthlyPayment.taxes)}
                </span>
              </div>
              <Progress 
                value={(mortgageData.monthlyPayment.taxes / mortgageData.monthlyPayment.total) * 100} 
                className="h-2"
              />
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium flex items-center gap-2">
                  Home Insurance
                  {displayedInsurance !== mortgageData.monthlyPayment.insurance && (
                    <Badge variant="outline" className="text-xs">Pine Ridge</Badge>
                  )}
                </span>
                <span className="font-semibold">
                  {formatCurrency(displayedInsurance)}
                </span>
              </div>
              <Progress 
                value={(displayedInsurance / adjustedMonthlyTotal) * 100} 
                className="h-2"
              />

              {mortgageData.monthlyPayment.pmi > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">PMI/MIP</span>
                    <span className="font-semibold">
                      {formatCurrency(mortgageData.monthlyPayment.pmi)}
                    </span>
                  </div>
                  <Progress 
                    value={(mortgageData.monthlyPayment.pmi / mortgageData.monthlyPayment.total) * 100} 
                    className="h-2"
                  />
                </>
              )}

              {mortgageData.monthlyPayment.hoa > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">HOA Fees</span>
                    <span className="font-semibold">
                      {formatCurrency(mortgageData.monthlyPayment.hoa)}
                    </span>
                  </div>
                  <Progress 
                    value={(mortgageData.monthlyPayment.hoa / mortgageData.monthlyPayment.total) * 100} 
                    className="h-2"
                  />
                </>
              )}
            </div>

            <Separator />
            
            <div className="flex justify-between items-center font-bold text-lg">
              <span>Total Monthly Payment</span>
              <span className="text-blue-600">
                {formatCurrency(adjustedMonthlyTotal)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* PMI Coverage Breakdown - only when PMI > 0 */}
        {mortgageData.monthlyPayment.pmi > 0 && (
          <PMICoverageBreakdown
            homePrice={mortgageData.homePrice}
            downPayment={mortgageData.downPayment}
            monthlyPMI={mortgageData.monthlyPayment.pmi}
            loanType={mortgageData.loanType}
            creditScore={mortgageData.creditScore}
          />
        )}

        {/* Loan Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Loan Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Loan Amount</p>
                <p className="text-xl font-bold">{formatCurrency(loanAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Loan Type</p>
                <Badge variant="outline" className="capitalize">
                  {mortgageData.loanType}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Interest Rate</p>
                <p className="text-xl font-bold">{formatInterestRate(mortgageData.interestRate)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Loan Term</p>
                <p className="text-xl font-bold">{mortgageData.loanTerm} years</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Loan-to-Value</p>
                <p className="text-xl font-bold">{loanToValue.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Cost</p>
                <p className="text-xl font-bold">{formatCurrency(totalCost)}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Home Price</span>
                {isEditingHomePrice ? (
                  <Input
                    ref={priceInputRef}
                    value={editingPriceValue}
                    onChange={(e) => setEditingPriceValue(formatCurrencyInput(e.target.value))}
                    onBlur={handleCommitPrice}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCommitPrice();
                      if (e.key === 'Escape') handleCancelEditPrice();
                    }}
                    className="w-40 h-8 text-right text-sm font-semibold"
                    autoFocus
                  />
                ) : (
                  <span
                    onClick={handleStartEditingPrice}
                    className={onHomePriceChange ? 'cursor-pointer group flex items-center gap-1 hover:text-primary transition-colors' : ''}
                  >
                    {formatCurrency(mortgageData.homePrice)}
                    {onHomePriceChange && (
                      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                    )}
                  </span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Down Payment ({mortgageData.downPaymentPercentage}%)</span>
                <span>{formatCurrency(mortgageData.downPayment)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Total Interest Paid</span>
                <span>{formatCurrency(totalInterest)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span className="text-sm">Total Amount Paid</span>
                <span>{formatCurrency(loanAmount + totalInterest + netClosingCosts)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Buydown Incentive Calculator */}
      {isCalculationComplete && (
        <BuydownIncentiveCalculator
          homePrice={mortgageData.homePrice}
          loanAmount={loanAmount}
          downPayment={mortgageData.downPayment}
          loanType={mortgageData.loanType}
          baseRate={mortgageData.interestRate}
          monthlyTaxes={mortgageData.monthlyPayment.taxes}
          monthlyInsurance={mortgageData.monthlyPayment.insurance}
          monthlyHoa={mortgageData.monthlyPayment.hoa}
          monthlyPmi={mortgageData.monthlyPayment.pmi}
          totalClosingCosts={mortgageData.closingCosts.total}
          earnestMoneyDeposit={mortgageData.earnestMoneyDeposit}
          onScenariosCalculated={setBuydownScenarios}
          onInsuranceChange={setDisplayedInsurance}
        />
      )}

      {/* Closing Disclosure Preview */}
      {isCalculationComplete && (
        <ClosingDisclosurePreview mortgageData={mortgageData} />
      )}

      {/* Status Indicator */}
      {isCalculationComplete && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="flex items-center gap-3 pt-6">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-semibold text-green-800">Calculation Complete</p>
              <p className="text-sm text-green-700">
                All calculations have been completed successfully. You can now generate a detailed report.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
